import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponseBase, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  MasterPayrollObject,
  PayrollConfigSetupData,
  EmployeePay,
  PayrollConfig,
  PayDateInfo,
  PayrollStatuses,
  Payline,
  PayGridSessionData,
  PayrollApprovalTotals,
  PayrollStartData,
  ManualCheck,
  CreatePayrollArgs,
  ScheduleRequest,
  Schedule,
  TimeclockModel,
  BillingCharge,
  AddPaylineRequestModel,
  BillingChargeRequest,
  SubmitApprovalRequestBody,
  TimeclockFile,
  TimeclockValidation,
  TimeclockFileUploadResult,
  PrefundInfo,
  PaylineType
} from '../models/payroll';
import moment from 'moment';
import { ApiResponse } from '../models/api-response';
import { Check, PayrollFrequency, PayrollInfo } from '../models/payroll-info';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PayrollService {
  private _clientSeeds: Record<string, string>;

  constructor(
    private http: HttpClient,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any,
    @Inject('loadingInterceptorSkipHeader') private loadingInterceptorSkipHeader: any
  ) {
    this._clientSeeds = {};
  }
  public addNewPayline(
    clientCode: string,
    year: number,
    seqNo: number,
    personId: number,
    reqBody: AddPaylineRequestModel
  ) {
    return this.http.post<ApiResponse<Payline[]>>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/Person/${personId}/Paylines`,
      reqBody
    );
  }

  public addPerson(clientCode: string, year: number, seqNo: number, personId: number) {
    return this.http.post<ApiResponse<EmployeePay>>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/Person/${personId}`,
      {}
    );
  }

  public calculatePaylines(
    clientCode: string,
    personId: number,
    year: number,
    seqNo: number,
    payLines: Payline[],
    previewOnly: boolean = false,
    showLoading?: boolean
  ): Observable<Payline[]> {
    let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');

    if (!showLoading) {
      headers = headers.set(this.loadingInterceptorSkipHeader, '');
    }

    let params = new HttpParams();
    params = params.append('previewOnly', previewOnly);

    return this.http.put<Payline[]>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/Person/${personId}/Paylines`,
      payLines,
      { params, headers }
    );
  }

  // NOTE: this should not be called directly, but from the payroll management service
  // so cached payroll data is cleared on cancellation
  public cancelPayroll(
    clientId: string,
    year: number,
    sequenceNumber: number,
    bypassErrorInterceptor = false
  ) {
    this._resetSeed(clientId);
    if (bypassErrorInterceptor) {
      let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
      return this.http.delete<HttpResponseBase>(
        `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}`,
        { headers }
      );
    } else {
      return this.http.delete<HttpResponseBase>(
        `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}`
      );
    }
  }

  public createPayGrid(clientCode: string, payroll: MasterPayrollObject): Observable<number> {
    return this.http.post<number>(`${this._clientUrl(clientCode)}/PayGrid`, payroll);
  }

  public createPayroll(args: CreatePayrollArgs): Observable<MasterPayrollObject> {
    this._resetSeed(args.ClientCode);

    const payDateYear = new Date(args.PayDate).getFullYear();

    return this.http.post<MasterPayrollObject>(
      `${this._clientUrl(args.ClientCode)}/Payroll/${payDateYear}`,
      args
    );
  }

  public createPayrollShadow(payroll: MasterPayrollObject): Observable<HttpResponseBase> {
    return this.http.post<HttpResponseBase>(
      `${this._clientUrl(payroll.ClientCode)}/Payroll/${payroll.Year}/${
        payroll.SequenceNumber
      }/Shadow`,
      payroll
    );
  }

  public deleteEmployeePaylines(
    clientCode: string,
    year: number,
    sequenceNum: number,
    id: number
  ): Observable<HttpResponseBase> {
    return this.http.delete<HttpResponseBase>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${sequenceNum}/Person/${id}/Paylines`
    );
  }

  public deletePayGrid(clientCode: string, id: number): Observable<HttpResponseBase> {
    return this.http.delete<HttpResponseBase>(`${this._clientUrl(clientCode)}/PayGrid/${id}`);
  }

  public approveTimeclockFile(clientCode: string, file: TimeclockFile) {
    return this.http.post<TimeclockModel>(`${this._clientUrl(clientCode)}/TimeClock/Approve`, file);
  }

  public deleteTimeclockFile(clientCode: string, id: number, persistedToAgile: boolean = false) {
    let params = new HttpParams();
    params = params.append('persistedToAgile', persistedToAgile.toString());
    return this.http.delete(`${this._clientUrl(clientCode)}/TimeClock/${id}`, { params });
  }

  public findTimeclockFile(clientId: string, periodEndDate: Date) {
    const d = new Date(periodEndDate);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const date = d.getDate().toString().padStart(2, '0');
    return this.http.get<ApiResponse<TimeclockModel>>(
      `${this._clientUrl(clientId)}/TimeClock?periodEndDate=${d.getFullYear()}${month}${date}`
    );
  }

  public getTimeclockDetails(clientCode: string, id: number, persistedToAgile: boolean) {
    let params = new HttpParams();
    params = params.append('persistedToAgile', persistedToAgile.toString());
    return this.http.get<TimeclockFile>(`${this._clientUrl(clientCode)}/TimeClock/${id}`, {
      params
    });
  }

  public undeleteTimeclockFile(clientCode: string, id: number) {
    return this.http.put<TimeclockModel>(
      `${this._clientUrl(clientCode)}/TimeClock/${id}/Restore`,
      {}
    );
  }

  public uploadTimeclockFile(clientCode: string, file: File, periodEnd: string) {
    const formData = new FormData();
    formData.append('fileUpload', file);
    let headers = new HttpHeaders();
    headers = headers.set(this.errorInterceptorSkipHeader, '');
    return this.http.post<TimeclockFileUploadResult>(
      `${this._clientUrl(clientCode)}/TimeClock/${periodEnd}`,
      formData,
      { headers }
    );
  }

  public validateTimeclockFile(clientCode: string, file: TimeclockFile) {
    return this.http.post<TimeclockValidation[]>(
      `${this._clientUrl(clientCode)}/TimeClock/${file.Id}/Validate`,
      file
    );
  }

  public findWorksheet(
    clientCode: string,
    locationId: number,
    calendarId: number,
    payDate: Date
  ): Observable<number> {
    let params = new HttpParams();
    params = params.append('pay_date', moment(payDate).format('YYYY-MM-DD'));
    params = params.append('calendar_id', calendarId.toString());

    return this.http.get<number>(
      `${this._clientUrl(clientCode)}/Location/${locationId}/FindWorksheet`,
      {
        params: params
      }
    );
  }

  public getEmployeePays(
    payDateInfo: PayDateInfo,
    config: PayrollConfig,
    calendarIds: number[]
  ): Observable<EmployeePay[]> {
    const employeePayReq = {
      Config: config,
      CalendarIds: calendarIds,
      LocationId: payDateInfo.LocationId,
      PeriodEnd: payDateInfo.PeriodEnd,
      PayDate: payDateInfo.TargetPayDate
    };

    return this.http.post<EmployeePay[]>(
      `${this._clientUrl(payDateInfo.ClientId)}/GetEmployeePays`,
      employeePayReq
    );
  }

  public getNextPayDate(
    clientCode: string,
    locationId: number,
    calendarId: number
  ): Observable<PayDateInfo> {
    return this.http.get<PayDateInfo>(
      `${this._clientUrl(clientCode)}/Location/${locationId}/Calendar/${calendarId}/NextPayDate`
    );
  }

  // get the paylines for a single employee pay record
  public getEmployeePayPaylines(
    clientCode: string,
    year: number,
    seqNo: number,
    personId: number
  ): Observable<ApiResponse<Payline[]>> {
    return this.http.get<ApiResponse<Payline[]>>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/Person/${personId}/Paylines`
    );
  }

  public getEmployeePaysHistory(
    clientId: string,
    year: string,
    seqNo: number,
    skipLoading: boolean = false
  ) {
    let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');

    if (skipLoading) {
      headers = headers.set(this.loadingInterceptorSkipHeader, '');
    }

    return this.http.get<EmployeePay[]>(
      `${this._clientUrl(clientId)}/CompletedPayrolls/${year}/${seqNo}/EmployeePaysHistory`,
      { headers }
    );
  }

  public getEmployeePaylinesHistory(clientId: string, year: string, seqNo: number) {
    return this.http.get<EmployeePay[]>(
      `${this._clientUrl(clientId)}/CompletedPayrolls/${year}/${seqNo}/EmployeePaylineHistory`
    );
  }

  public getInitialPayrollState(clientId: string, year: number, seqNo: number) {
    return this.http.get<MasterPayrollObject>(
      `${this._clientUrl(clientId)}/InitialPayrollState/${year}/${seqNo}`
    );
  }

  public getCompletedPayroll(clientId: string, year: string, seqNo: number) {
    return this.http.get<ApiResponse<MasterPayrollObject>>(
      `${this._clientUrl(clientId)}/CompletedPayrolls/${year}/${seqNo}`
    );
  }

  public getChecksToPrint(clientId: string, year: string, seqNo: number, checkNumbers: string[]) {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');
    headers = headers.set(this.errorInterceptorSkipHeader, '');
    return this.http.post(
      `${this._clientUrl(clientId)}/CompletedPayrolls/${year}/${seqNo}/Checks`,
      { CheckNumbers: checkNumbers },
      { headers, responseType: 'blob' as 'json' }
    );
  }

  public getOutstandingTaxObligation(clientCode: string, year: number, seqNo: number) {
    return this.http.get<number>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/OutstandingTaxObligation`
    );
  }

  public getPayroll(
    clientId: string,
    year: number,
    sequenceNumber: number
  ): Observable<MasterPayrollObject> {
    return this.http.get<MasterPayrollObject>(
      `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}`
    );
  }

  public getPayGrid(clientCode: string, id: number): Observable<MasterPayrollObject> {
    return this.http.get<MasterPayrollObject>(`${this._clientUrl(clientCode)}/PayGrid/${id}`);
  }

  public getPayrollConfigSetupData(
    clientCode,
    calendars: ScheduleRequest[],
    locationId: number,
    payDate: Date,
    payType: number
  ): Observable<PayrollConfigSetupData> {
    const configReq = {
      Calendars: calendars,
      PayDate: payDate,
      PayType: payType,
      LocationId: locationId
    };

    return this.http.post<PayrollConfigSetupData>(
      `${this._clientUrl(clientCode)}/GetPayrollConfigSetupData`,
      configReq
    );
  }

  public getPayrollEmployeePays(
    clientCode: string,
    year: number,
    seqNo: number
  ): Observable<ApiResponse<EmployeePay[]>> {
    return this.http.get<ApiResponse<EmployeePay[]>>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seqNo}/EmployeePays`
    );
  }

  public getPayrollHistoryByEmployee(
    clientCode: string,
    personId: number,
    startDate: string,
    endDate: string
  ) {
    return this.http.get<MasterPayrollObject[]>(
      `${this._clientUrl(
        clientCode
      )}/CompletedPayrolls/Person/${personId}?startDate=${startDate}&endDate=${endDate}`
    );
  }

  public getPayrollInfo(clientCode: string, year: number, sequence_num: number) {
    return this.http.get<PayrollInfo>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${sequence_num}/PayrollInfo`
    );
  }

  public getCompletedPayrollThirdPartyChecks(
    clientCode: string,
    year: number,
    sequenceNumber: number
  ) {
    return this.http.get<Check[]>(
      `${this._clientUrl(clientCode)}/CompletedPayrolls/${year}/${sequenceNumber}/ThirdPartyChecks`
    );
  }

  public isInPrefundWindow(
    clientCode: string,
    year: number,
    sequenceNumber: number
  ): Observable<PrefundInfo> {
    return this.http
      .get<PrefundInfo>(
        `${this._clientUrl(clientCode)}/Payroll/${year}/${sequenceNumber}/IsInPrefundWindow`
      )
      .pipe(
        map(x => {
          const startString: any = x.PrefundStartDate;
          const checkTimeString: any = x.CheckTime;

          return {
            IsInPrefund: x.IsInPrefund,
            PrefundStartDate: new Date(startString),
            CheckTime: new Date(checkTimeString)
          };
        })
      );
  }

  public getPayrollStartData(
    clientCode: string,
    disableCaching?: boolean | undefined
  ): Observable<PayrollStartData> {
    //We want to cache these requests
    let headers = new HttpHeaders();
    let params = new HttpParams();

    if (disableCaching == null || !disableCaching) {
      headers = headers.set('X-Cache-Lifetime', '5');
      var seed = this._getSeed(clientCode);
      params = params.set('cache', seed);
    }

    return this.http.get<PayrollStartData>(`${this._clientUrl(clientCode)}/PayrollStartData`, {
      headers: headers,
      params: params
    });
  }

  public getPrefundWarnings(payDateInfo: PayDateInfo, calendarIds: number[], payType: number) {
    const createPayrollArgs = {
      CalendarIds: calendarIds,
      LocationSeq: payDateInfo.LocationId,
      PeriodEnd: payDateInfo.PeriodEnd,
      PayDate: payDateInfo.TargetPayDate,
      PayType: payType
    };

    const payDateYear = new Date(payDateInfo.TargetPayDate).getFullYear();

    return this.http.post<string[]>(
      `${this._clientUrl(payDateInfo.ClientId)}/Payroll/${payDateYear}/GetPrefundWarnings`,
      createPayrollArgs
    );
  }

  public async initializePayGrid(
    clientCode,
    calendars: Schedule[],
    locationId: number,
    payDate: Date,
    payType: number,
    payFrequency: PayrollFrequency = null,
    createdBy: string
  ): Promise<MasterPayrollObject> {
    let config = new PayrollConfig();

    let employeePays = await this._getEmployeePays(
      clientCode,
      config,
      calendars.map(x => x.CalendarId),
      locationId,
      calendars[0].PeriodEnd,
      payDate
    );

    // Task 7532 add pay frequency (of the calendar) to each payline
    if (employeePays && employeePays.length && payFrequency) {
      employeePays.forEach(pay => {
        pay.Paylines.forEach(line => {
          line.PayFrequency = payFrequency;
        });
      });
    }

    const payroll = new MasterPayrollObject(
      clientCode,
      calendars,
      locationId,
      payDate,
      false,
      false,
      PayrollStatuses.Started,
      createdBy,
      new Date()
    );
    payroll.Config = config;
    payroll.EmployeePays = employeePays;
    return payroll;
  }

  public isPaylineHidden(payline: Payline) {
    return payline.PaylineType == PaylineType.NoCheck && !payline.UserConfirmedNonRegularPayline;
  }

  public setInitialPaylineState(
    clientId: string,
    year: number,
    seqNo: number,
    status: number,
    personId: number,
    line: Payline
  ) {
    return this.http.post<Payline>(
      `${this._clientUrl(clientId)}/Payroll/${year}/${seqNo}/${status}/Person/${personId}/Shadow`,
      line
    );
  }

  public startManualPayroll(clientCode: string, year: number, locationSequenceNumber: number) {
    this._resetSeed(clientCode);

    return this.http.post<MasterPayrollObject>(
      `${this._clientUrl(clientCode)}/ManualPayroll/${year}`,
      {
        locationSeqNumber: locationSequenceNumber
      }
    );
  }

  public saveManualPayroll(clientCode: string, year: number, sequenceNumber: number) {
    this._resetSeed(clientCode);

    let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');

    return this.http.post(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${sequenceNumber}/SaveManualPayroll`,
      { headers }
    );
  }

  public submitApproval(
    clientCode: string,
    year: number,
    sequenceNumber: number,
    suppressNotifications?: boolean
  ): Observable<ApiResponse<PayrollApprovalTotals>> {
    this._resetSeed(clientCode);

    let req = {};

    if (suppressNotifications != null) {
      req = new SubmitApprovalRequestBody(suppressNotifications);
    }

    return this.http.post<ApiResponse<PayrollApprovalTotals>>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${sequenceNumber}/SubmitApproval`,
      req
    );
  }

  public updateBillingCharges(
    clientCode: string,
    year: number,
    seq: number,
    request: BillingChargeRequest
  ) {
    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(clientCode)}/Payroll/${year}/${seq}/Billings`,
      request
    );
  }

  public updatePayGrid(
    clientCode: string,
    payGridId: number,
    payroll: MasterPayrollObject,
    skipLoading = false
  ): Observable<HttpResponseBase> {
    let headers = new HttpHeaders();

    if (skipLoading) {
      headers = headers.set(this.loadingInterceptorSkipHeader, '');
    }

    headers = headers.set(this.errorInterceptorSkipHeader, '');

    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(clientCode)}/PayGrid/${payGridId}`,
      payroll,
      { headers }
    );
  }

  public updatePayrollOwner(clientId: string, year: number, sequenceNumber: number) {
    let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');

    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}/PayrollUserId`,
      {},
      { headers }
    );
  }

  public updatePayrollStatus(
    clientId: string,
    year: number,
    sequenceNumber: number,
    status: number
  ) {
    let headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');

    return this.http.put<HttpResponseBase>(
      `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}/Status`,
      {
        Status: status
      },
      { headers }
    );
  }

  // #region PayGridSessionData

  public createPayGridSessionData(model: PayGridSessionData) {
    return this.http.post(
      `${this._clientUrl(model.ClientId)}/Payroll/${model.PayrollYear}/${
        model.PayrollSequenceNumber
      }/PayGridSessionData`,
      model
    );
  }

  public deletePayGridSessionData(clientId: string, year: number, sequenceNumber: number) {
    return this.http.delete(
      `${this._clientUrl(clientId)}/Payroll/${year}/${sequenceNumber}/PayGridSessionData`
    );
  }

  public getPayGridSessionData(clientId: string, locationId: number, year: number, seqNo: number) {
    return this.http.get<PayGridSessionData>(
      `${this._clientUrl(
        clientId
      )}/Locations/${locationId}/Payroll/${year}/${seqNo}/PayGridSessionData`
    );
  }

  public updatePayGridSessionData(model: PayGridSessionData, locationId: number) {
    let headers = new HttpHeaders().set(this.loadingInterceptorSkipHeader, '');

    return this.http.put(
      `${this._clientUrl(model.ClientId)}/Locations/${locationId}/Payroll/${model.PayrollYear}/${
        model.PayrollSequenceNumber
      }/PayGridSessionData`,
      model,
      { headers }
    );
  }

  // #endregion

  // #region PRIVATE  METHODS

  private _clientUrl(clientId: string) {
    return `${this.env.apiUrlRoot}/Companies/${clientId}`;
  }

  private _getEmployeePays(
    clientCode,
    config: PayrollConfig,
    calendarIds: number[],
    locationId: number,
    periodEnd: Date,
    payDate: Date
  ): Promise<EmployeePay[]> {
    const employeePayReq = {
      Config: config,
      CalendarIds: calendarIds,
      LocationId: locationId,
      PeriodEnd: periodEnd,
      PayDate: payDate
    };
    return this.http
      .post<EmployeePay[]>(`${this._clientUrl(clientCode)}/GetEmployeePays`, employeePayReq)
      .toPromise()
      .then(pay => {
        return pay;
      });
  }

  private _getSeed(clientCode: string) {
    if (!this._clientSeeds[clientCode]) {
      this._resetSeed(clientCode);
    }

    return this._clientSeeds[clientCode];
  }

  private _resetSeed(clientCode: string) {
    const length = 8;
    // 1 byte => 2 hex characters
    var bytes = new Uint8Array(length / 2);

    // fill with random data
    crypto.getRandomValues(bytes);

    // parse numbers as hex strings
    var text = [...bytes].map(n => n.toString(16).padStart(2, '0')).join('');

    this._clientSeeds[clientCode] = text;
  }
  // #endregion
}
